import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, IndianRupee, FileText, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import Spinner from './Spinner.js';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell 
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

const StatCard = ({ title, value, icon, color, trend }) => (
    <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flex: 1,
        minWidth: '200px'
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
            <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            {trend && (
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: trend > 0 ? '#10b981' : '#ef4444' }}>
                    {trend > 0 ? '+' : ''}{trend}%
                </span>
            )}
        </div>
        <h3 style={{ margin: 0, fontSize: '14px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
        <p style={{ margin: '5px 0 0', fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
            ₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </p>
    </div>
);

const AccountsDashboard = ({ db }) => {
    const [stats, setStats] = useState({
        totalRevenue: 0,
        totalExpenses: 0,
        pendingReceivables: 0,
        cashFlow: 0
    });
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [allTransactions, setAllTransactions] = useState([]);
    const [upcomingInvoices, setUpcomingInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        
        // Listener for transactions
        const transQ = query(collection(db, "accounts_transactions"));
        const unsubscribeTrans = onSnapshot(transQ, (snapshot) => {
            const transactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            let rev = 0;
            let exp = 0;
            transactions.forEach(t => {
                if (t.type === 'revenue') rev += t.amount;
                else exp += t.amount;
            });

            setAllTransactions(transactions);
            setRecentTransactions(transactions.sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0, 5));
            setStats(prev => ({
                ...prev,
                totalRevenue: rev,
                totalExpenses: exp,
                cashFlow: rev - exp
            }));
            setLoading(false);
        });

        // Listener for invoices
        const invQ = query(collection(db, "accounts_invoices"));
        const unsubscribeInvoices = onSnapshot(invQ, (snapshot) => {
            const invoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            let pending = 0;
            invoices.forEach(i => {
                if (i.status !== 'paid') pending += (i.balance || 0);
            });

            setUpcomingInvoices(invoices.filter(i => i.status !== 'paid').sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate)).slice(0, 5));
            setStats(prev => ({
                ...prev,
                pendingReceivables: pending
            }));
        });

        return () => {
            unsubscribeTrans();
            unsubscribeInvoices();
        };
    }, [db]);

    // Process data for Revenue vs Expenses Bar Chart (last 6 months)
    const chartData = useMemo(() => {
        const months = {};
        const today = new Date();
        
        for (let i = 5; i >= 0; i--) {
            const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
            const key = d.toLocaleString('default', { month: 'short', year: '2-digit' });
            months[key] = { name: key, revenue: 0, expenses: 0 };
        }

        allTransactions.forEach(t => {
            const date = new Date(t.date);
            const key = date.toLocaleString('default', { month: 'short', year: '2-digit' });
            if (months[key]) {
                if (t.type === 'revenue') months[key].revenue += t.amount;
                else months[key].expenses += t.amount;
            }
        });

        return Object.values(months);
    }, [allTransactions]);

    // Process data for Expense Pie Chart
    const pieData = useMemo(() => {
        const categories = {};
        allTransactions.filter(t => t.type === 'expense').forEach(t => {
            categories[t.category] = (categories[t.category] || 0) + t.amount;
        });

        return Object.entries(categories)
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [allTransactions]);

    if (loading) return <Spinner />;

    return (
        <div style={{ paddingBottom: '50px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ margin: 0 }}>Accounts Overview</h2>
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '40px' }}>
                <StatCard title="Total Revenue" value={stats.totalRevenue} icon={<TrendingUp size={20}/>} color="#10b981" />
                <StatCard title="Total Expenses" value={stats.totalExpenses} icon={<TrendingDown size={20}/>} color="#ef4444" />
                <StatCard title="Pending Receivables" value={stats.pendingReceivables} icon={<Clock size={20}/>} color="#f59e0b" />
                <StatCard title="Net Cash Flow" value={stats.cashFlow} icon={<IndianRupee size={20}/>} color="#2563eb" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px', marginBottom: '40px' }}>
                {/* Revenue vs Expenses Chart */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 20px', fontSize: '16px' }}>Revenue vs Expenses (Trend)</h3>
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(val) => `₹${val/1000}k`} />
                                <Tooltip 
                                    formatter={(value) => [`₹${value.toLocaleString()}`, '']}
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                                />
                                <Legend iconType="circle" />
                                <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} name="Revenue" />
                                <Bar dataKey="expenses" fill="#ef4444" radius={[4, 4, 0, 0]} name="Expenses" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Expense Breakdown Pie Chart */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: '0 0 20px', fontSize: '16px' }}>Expense Breakdown</h3>
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
                                <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' }}>
                {/* Recent Transactions */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px' }}>Recent Transactions</h3>
                        <Link to="/accounts/transactions" style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}>View All</Link>
                    </div>
                    {recentTransactions.map(t => (
                        <Link 
                            key={t.id} 
                            to={`/accounts/transactions?highlight=${t.id}`}
                            style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9', textDecoration: 'none', color: 'inherit' }}
                            className="hover-highlight"
                        >
                            <div style={{ 
                                width: '8px', height: '8px', borderRadius: '50%', 
                                backgroundColor: t.type === 'revenue' ? '#10b981' : '#ef4444',
                                marginRight: '15px'
                            }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: '600' }}>{t.category}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                    {t.entityName || 'N/A'} • {new Date(t.date).toLocaleDateString()}
                                </div>
                            </div>
                            <div style={{ fontWeight: 'bold', color: t.type === 'revenue' ? '#059669' : '#dc2626' }}>
                                {t.type === 'revenue' ? '+' : '-'}₹{t.amount.toLocaleString()}
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Upcoming Dues */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px' }}>Upcoming Receivables</h3>
                        <Link to="/accounts/invoices" style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}>Manage Invoices</Link>
                    </div>
                    {upcomingInvoices.map(i => {
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const dueDate = new Date(i.dueDate);
                        dueDate.setHours(0,0,0,0);

                        const isOverdue = i.balance > 0 && dueDate < today;
                        const statusColor = isOverdue ? '#ef4444' : '#64748b';
                        const statusText = isOverdue ? '(OVERDUE)' : '';

                        return (
                            <Link 
                                key={i.id} 
                                to={`/accounts/invoices?highlight=${i.id}`}
                                style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9', textDecoration: 'none', color: 'inherit' }}
                                className="hover-highlight"
                            >
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{i.customerName}</div>
                                    <div style={{ fontSize: '12px', color: statusColor }}>
                                        Due: {new Date(i.dueDate).toLocaleDateString()} {statusText}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 'bold' }}>₹{i.balance.toLocaleString()}</div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#94a3b8' }}>Inv: {i.invoiceNo}</div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default AccountsDashboard;
